import * as React from 'react';
import withStyles from 'react-jss';
import { StylesProps, KeyLabel } from 'src/Library/Types';
import {
  Row,
  Form,
  Col,
  Select,
  AutoComplete,
  Radio,
  Collapse,
  Modal,
  Button,
  Icon,
} from 'src/Library/AntdLibComponents';
import { useGenericState } from 'src/Library/UseGenericState';
import {
  renderSelectDropDown, renderLabelAndInput, renderLabelAndTextArea, renderLabelAndInputNumber,
} from 'src/Library/Common';
import { connect } from 'react-redux';
import { isValidArray, isValidNumber } from 'src/Library/Helper';
import { Form13Styles } from './Form13Request.styles';
import { ContainerList } from 'src/components/common/Types/CommonTypes';
import { CargoType } from 'src/components/pages/Enquiry/InquiryUtils';
import classNames from 'classnames';
import { arrayToObject } from 'src/utils/utils';
import { FORM13_CARGO_TYPES } from 'src/components/pages/shipment-execution/SE.utils';
import Asterisk from 'src/components/common/Asterisk';

interface IProps extends StylesProps<ReturnType<typeof Form13Styles>> {
  form;
  containerTypes: KeyLabel[];
  cargoToContainerListMap;
  containerKey: number;
  containerDetails;
  containerNoList;
  getContainers;
  isFetchingContainers: boolean;
  containerSizes: string[];
  selectedCargoType: string;
  renderLabelAndField;
  isLoading: boolean;
  handleRemoveContainer: (containerKey) => void;
  isAdditionalHazNumbersSelected: boolean;
  imoClassCodes: KeyLabel [];
  productIMOClassCodeMap: KeyLabel;
  mandatoryFields;
  odcUnits?: string[];
}

interface StateProps {
  isRemoveContainerModalVisible?: boolean;
}

const Form13UploadContainerDetails = (props: IProps): React.ReactElement => {

  const {
    classes,
    form,
    containerTypes,
    cargoToContainerListMap,
    containerKey,
    containerDetails,
    containerNoList,
    getContainers,
    isFetchingContainers,
    containerSizes,
    selectedCargoType,
    renderLabelAndField,
    isLoading,
    handleRemoveContainer,
    isAdditionalHazNumbersSelected,
    imoClassCodes,
    productIMOClassCodeMap,
    mandatoryFields,
    odcUnits,
  } = props;

  const [state, setState] = useGenericState<StateProps>({ });

  const {
    isRemoveContainerModalVisible,
  } = state;

  const {
    getFieldDecorator,
    getFieldValue,
    setFieldsValue,
  } = form;

  const {
    isPackageCountMandatory,
    isVehicleNoMandatory,
  } = mandatoryFields;

  const labelInputProps = {
    colSpan: {
      left: 9,
      right: 14,
    },
    form,
    classes,
    precision: 2,
    min: 0,
    disabled: isLoading,
    formStyles: { marginBottom: 0 },
  };
  const getContainerNumbers = () => {
    const queryString = getFieldValue(`containerDetails[${containerKey}].containerNumber`);
    const containerDetailsList = getFieldValue('containerDetails');
    const filledContainerNumbers = isValidArray(containerDetailsList) ?
      containerDetailsList?.map(item => item.containerNumber) : [];

    if (!queryString) {
      return containerNoList?.filter(
        item => filledContainerNumbers?.indexOf(item?.containerNumber) === -1);
    }

    const filteredContainers = containerNoList?.filter(
      item => filledContainerNumbers?.indexOf(item?.containerNumber) === -1 &&
      item?.containerNumber?.toLowerCase()?.includes(queryString?.toLowerCase()));
    return filteredContainers;
  };

  const handleContainerNumberSelection = (options, containerKey) => {
    setFieldsValue({
      [`containerDetails[${containerKey}].containerSize`]: options?.props?.containerSize,
      [`containerDetails[${containerKey}].containerType`]: options?.props?.containerType,
      [`containerDetails[${containerKey}].agentSealNo`]: undefined,
      [`containerDetails[${containerKey}].customSealNo`]: undefined,
      [`containerDetails[${containerKey}].shipBillInvoiceNo`]: undefined,
      [`containerDetails[${containerKey}].vgmViaOdex`]: 'YES',
      [`containerDetails[${containerKey}].weight`]: undefined,
      [`containerDetails[${containerKey}].packageCount`]: undefined,
      [`containerDetails[${containerKey}].doNo`]: undefined,
      [`containerDetails[${containerKey}].temperature`]: undefined,
      [`containerDetails[${containerKey}].voltage`]: undefined,
      [`containerDetails[${containerKey}].vehicleNo`]: undefined,
      [`containerDetails[${containerKey}].haulier`]: undefined,
      [`containerDetails[${containerKey}].driverName`]: undefined,
      [`containerDetails[${containerKey}].driverLicenseNo`]: undefined,
      [`containerDetails[${containerKey}].remarks`]: undefined,
      [`containerDetails[${containerKey}].imoNo`]: undefined,
      [`containerDetails[${containerKey}].unNo`]: undefined,
      [`containerDetails[${containerKey}].imoNo1`]: undefined,
      [`containerDetails[${containerKey}].unNo1`]: undefined,
      [`containerDetails[${containerKey}].imoNo2`]: undefined,
      [`containerDetails[${containerKey}].unNo2`]: undefined,
      [`containerDetails[${containerKey}].imoNo3`]: undefined,
      [`containerDetails[${containerKey}].unNo3`]: undefined,
      [`containerDetails[${containerKey}].leftDimensions`]: undefined,
      [`containerDetails[${containerKey}].rightDimensions`]: undefined,
      [`containerDetails[${containerKey}].topDimensions`]: undefined,
      [`containerDetails[${containerKey}].backDimensions`]: undefined,
      [`containerDetails[${containerKey}].frontDimensions`]: undefined,
      [`containerDetails[${containerKey}].odcUnits`]: undefined,
    });
  }

  const renderContainerNumber = () : React.ReactElement => {
    const containerNumbers = getContainerNumbers();
    return (
      <Form.Item style={{ marginBottom: '12px' }}>
        {getFieldDecorator(`containerDetails[${containerKey}].containerNumber`, {
          rules: [
            { required: true, message: 'Required' },
            { pattern: /^[a-zA-Z]{4}[0-9]{7}$/, message: 'Must contain 4 alphabets followed by 7 numbers' }
          ],
          initialValue: containerDetails?.[containerKey]?.containerNumber,
        })(
          <AutoComplete
            style={{ fontSize: '12px', width: '100%' }}
            placeholder="Enter Container No"
            onFocus={() => getContainers()}
            disabled={isLoading}
            dropdownStyle={{ zIndex: 2000 }}
            dropdownRender={menu => renderSelectDropDown(menu, isFetchingContainers)}
            onSelect={(val, options) => handleContainerNumberSelection(options, containerKey)}
          >
            {containerNumbers?.map((container) => (
              <AutoComplete.Option
                key={container?.containerNumber}
                { ...container }
                value={container?.containerNumber}
              >
                {container?.containerNumber}
              </AutoComplete.Option>
            ))}
          </AutoComplete>
        )}
      </Form.Item>
    );
  };

  const handleCloseConfirmation = () : void => {
    setState({ isRemoveContainerModalVisible: false });
  };

  const renderRemoveModalConfirmation = () : React.ReactElement => {
    return (
      <Modal
        title={
          <span><Icon type="exclamation-circle" theme="filled" style={{
            fontSize: '16px',
            color: '#FAB826',
            marginRight: '12px',
          }}/>Confirm Action</span>
        }
        width={400}
        zIndex={2000}
        visible={isRemoveContainerModalVisible}
        footer={
          <React.Fragment>
            <span style={{
              cursor: 'pointer',
              fontSize: '12px',
              marginRight: '16px',
            }} onClick={handleCloseConfirmation}>Cancel</span>
            <Button
              onClick={() => {
                handleRemoveContainer(containerKey);
                handleCloseConfirmation();
              }}
              className={classes.addContainerButton}
            >
              Submit
            </Button>
          </React.Fragment>
        }
        onCancel={handleCloseConfirmation}
      >
        <div style={{ fontSize: '16px', marginBottom: '25px' }}>
          This will remove all the
          <strong> {`Container ${containerKey + 1}`} </strong>
          details. Are you sure you want to proceed?
        </div>
      </Modal>
    );
  };

  const renderContainerSize = (containerKey) : React.ReactElement => {
    return (
      <Form.Item style={{ marginBottom: '12px' }}>
        {getFieldDecorator(`containerDetails[${containerKey}].containerSize`, {
          rules: [{ required: true, message: 'Required' }],
          initialValue: containerDetails?.[containerKey]?.containerSize,
        })(
          <Select
            getPopupContainer={(trigger: any) => trigger.parentNode}
            className={classes.selectField}
            showSearch
            allowClear
            disabled={isLoading}
            optionFilterProp="children"
            placeholder="Select Container Size"
          >
            {containerSizes?.map(item =>
              <Select.Option key={item}>{item}</Select.Option>
            )}
          </Select>
        )}
      </Form.Item>
    );
  };

  const renderContainerNumberField = () : React.ReactElement => {;
    return renderLabelAndField(
      <>Container No<Asterisk /></>,
      renderContainerNumber(),
      {
        left: 9,
        right: 14,
      },
    );
  };

  const renderContainerSizeField = () : React.ReactElement => {;
    return renderLabelAndField(
      <>Container Size<Asterisk /></>,
      renderContainerSize(containerKey),
      {
        left: 9,
        right: 14,
      },
    );
  };

  const renderContainerTypeField = () : React.ReactElement => {
    const cargoType = selectedCargoType;
    const cargoWiseContainerList = containerTypes?.filter(item => (
      cargoToContainerListMap?.[cargoType]?.indexOf(item.key) !== -1
    ));
    const field = <Form.Item style={{ marginBottom: '12px' }}>
      {getFieldDecorator(`containerDetails[${containerKey}].containerType`, {
        initialValue: containerDetails?.[containerKey]?.containerType,
        rules: [{ required: true, message: 'Required' }],
      })(
        <Select
          getPopupContainer={(trigger: any) => trigger.parentNode}
          className={classes.selectField}
          showSearch
          allowClear
          disabled={isLoading}
          optionFilterProp="children"
          placeholder={'Select Container Type'}
          dropdownMatchSelectWidth={false}
        >
          {cargoWiseContainerList?.map(item =>
            <Select.Option key={item.key}>{item.label}</Select.Option>
          )}
        </Select>
      )}
    </Form.Item>;
    return renderLabelAndField(
      <>Container Type<Asterisk /></>,
      field,
      {
        left: 9,
        right: 15,
      },
    );
  };

  const renderAgentSealNo = () : React.ReactElement => {
    return (
      renderLabelAndInput({
        ...labelInputProps,
        label: <>Agent Seal No<Asterisk /></>,
        key: `containerDetails[${containerKey}].agentSealNo`,
        placeholder: 'Enter Agent Seal No',
        initialValue: containerDetails?.[containerKey]?.agentSealNo,
        rules: [
          { required: true, message: 'Required' },
          { max: 20, message: 'Maximum 20 characters allowed' },
        ],
      })
    );
  };

  const renderCustomSealNo = () : React.ReactElement => {
    return (
      renderLabelAndInput({
        ...labelInputProps,
        label: <>Custom Seal No<Asterisk /></>,
        key: `containerDetails[${containerKey}].customSealNo`,
        placeholder: 'Enter Custom Seal No',
        initialValue: containerDetails?.[containerKey]?.customSealNo,
        rules: [
          { required: true, message: 'Required' },
          { max: 20, message: 'Maximum 20 characters allowed' },
        ],
      })
    );
  };

  const renderShipBillInvoiceNo = () : React.ReactElement => {
    return (
      renderLabelAndInput({
        ...labelInputProps,
        colSpan: {
          left: 9,
          right: 15,
        },
        label: <>SB no or Invoice no<Asterisk /></>,
        key: `containerDetails[${containerKey}].shipBillInvoiceNo`,
        placeholder: 'Enter SB no or Invoice no',
        initialValue: containerDetails?.[containerKey]?.shipBillInvoiceNo,
        rules: [
          { required: true, message: 'Required' },
          { max: 22, message: 'Maximum 22 characters allowed' },
        ],
        labelStyles: { marginTop: '3px' },
      })
    );
  };

  const renderVGMWeight = () : React.ReactElement => {
    const isVgmViaOdex = getFieldValue(`containerDetails[${containerKey}].vgmViaOdex`) === 'YES';
    return (
      renderLabelAndInputNumber({
        ...labelInputProps,
        colSpan: {
          left: 9,
          right: 14,
        },
        label: <>VGM (MT) {isVgmViaOdex ? null : <Asterisk />}</>,
        key: `containerDetails[${containerKey}].weight`,
        placeholder: 'Enter VGM (MT)',
        initialValue: containerDetails?.[containerKey]?.weight,
        max: 999.99,
        rules: [
          {
            validator: (rule, value, cb) => {
              if (!isValidNumber(value) && !isVgmViaOdex) {
                cb('Required');
              } else if (value < 0) {
                cb('Must be positive');
              } else if (value > 999.99) {
                cb('Maximum 999.99 allowed');
              } else cb();
            },
          },
        ],
        disabled: isVgmViaOdex,
        labelStyles: { marginTop: '3px' },
      })
    );
  };

  const renderVgmViaOdex = () : React.ReactElement => {
    const VgmViaOdex = [
      { key: 'YES', label: 'Yes' },
      { key: 'NO', label: 'No' },
    ];
    const field = (
      <Form.Item style={{ marginBottom: 0 }}>
        {getFieldDecorator(`containerDetails[${containerKey}].vgmViaOdex`, {
          initialValue: containerDetails?.[containerKey]?.vgmViaOdex || 'YES',
        })(
          <Radio.Group className={classes.flexRow}
            onChange={e => {
              if (e.target.value === 'YES') {
                setFieldsValue({
                  [`containerDetails[${containerKey}].weight`]: undefined,
                });
              }
            }}
          >
            {VgmViaOdex?.map(item  => (
              <Radio value={item.key} style={{ margin: '12px 0', fontSize: '12px' }} key={item.key}>
                {item.label}
              </Radio>
            ))}
          </Radio.Group>
        )}
      </Form.Item>
    );
    return renderLabelAndField(
      <>VGM Via Odex<Asterisk /></>,
      field,
      {
        left: 9,
        right: 14,
      },
    );
  };

  const imoNoMap = arrayToObject(imoClassCodes, 'key');

  const getRemainingImoNoList = () => {
    const formVaues = getFieldValue(`containerDetails[${containerKey}]`);
    const imoNoAdded = [formVaues?.imoNo, formVaues?.imoNo1, formVaues?.imoNo2, formVaues?.imoNo3];
    imoNoAdded?.forEach(item => {
      if (item?.key) {
        delete imoNoMap[item?.key]
      }
    });
    return imoNoMap;
  };

  const renderIMONo = (props) : React.ReactElement => {
    const remainingImoNoList = getRemainingImoNoList();

    const {
      key,
      rules,
      initialValue,
      label,
      colSpan,
    } = props;

    const field = (
      <Form.Item style={{ marginBottom: '12px' }}>
        {getFieldDecorator(key, {
          rules,
          initialValue,
        })(
          <Select
            getPopupContainer={(trigger: any) => trigger.parentNode}
            className={classes.selectField}
            showSearch
            allowClear
            disabled={isLoading}
            labelInValue
            optionFilterProp="children"
            placeholder="Select IMO No."
            dropdownMatchSelectWidth={false}
          >
            {remainingImoNoList && Object.keys(remainingImoNoList)?.map(item =>
              <Select.Option key={`${remainingImoNoList[item].key}`}>
                {remainingImoNoList[item].label}
              </Select.Option>
            )}
          </Select>
        )}
      </Form.Item>
    );
    return renderLabelAndField(
      label,
      field,
      colSpan ? colSpan : {
        left: 9,
        right: 14,
      },
    );
  }

  const renderUnNo = (props) : React.ReactElement => {
    const {
      key,
      isRequired,
      initialValue,
      label,
      colSpan,
    } = props;
    return (
      renderLabelAndInputNumber({
        ...labelInputProps,
        ...(colSpan && { colSpan }),
        min: 1,
        max: 3600,
        precision: 0,
        label,
        key,
        placeholder: 'Enter UN No',
        initialValue,
        rules: [
          {
            validator: (rule, value, cb) => {
              if (!isValidNumber(value) && isRequired) {
                cb('Required');
              } else if (isValidNumber(value) && value < 1) {
                cb('Minimum 1 allowed');
              } else if (isValidNumber(value) && value > 3600) {
                cb('Maximum 3600 allowed');
              } else cb();
            },
          },
        ],
        labelStyles: { marginTop: '3px' },
      })
    );
  };

  const renderODCDimensions = (props) : React.ReactElement => {
    const {
      key,
      initialValue,
      label,
    } = props;
    return (
      renderLabelAndInputNumber({
        ...labelInputProps,
        colSpan: {
          left: 10,
          right: 10,
        },
        max: 999.99,
        precision: 2,
        label,
        key,
        placeholder: 'Enter Value',
        initialValue,
        rules: [
          {
            validator: (rule, value, cb) => {
              if (!isValidNumber(value)) {
                cb('Required');
              } else if (value > 999.99) {
                cb('Maximum 999.99 allowed');
              } else cb();
            },
          },
        ],
        labelStyles: { marginTop: '3px' },
        formStyles: { marginBottom: 0, paddingLeft: '5px' },
      })
    );
  };

  const renderContainerDetails = () : React.ReactElement => {
    const isHaz = selectedCargoType === FORM13_CARGO_TYPES.HAZARDOUS;
    const isODC = selectedCargoType === FORM13_CARGO_TYPES.ODC;
    return (
      <div className={classes.containerDiv} style={{ borderBottom: 0 }}>
        <div className={classNames(classes.contentHeading, classes.justifySpaceBetween)}>
          Container {containerKey + 1}
          <div>
            {containerDetails?.length > 1 &&
              <Icon
                type="delete"
                style={{ fontSize: '16px' }}
                onClick={() => setState({ isRemoveContainerModalVisible: true })}
              />
            }
          </div>
        </div>              
        <Row style={{ paddingTop: '8px' }}>
          <Col span={7}>
            {renderContainerNumberField()}
          </Col>
          <Col span={1} />
          <Col span={7}>
            {renderContainerSizeField()}
          </Col>
          <Col span={1} />
          <Col span={7}>
            {renderContainerTypeField()}
          </Col>
        </Row>
        <Row>
          <Col span={7}>
            {renderAgentSealNo()}
          </Col>
          <Col span={1} />
          <Col span={7}>
            {renderCustomSealNo()}
          </Col>
          <Col span={1} />
          <Col span={7}>
            {renderShipBillInvoiceNo()}
          </Col>
        </Row>
        <Row style={{ paddingTop: '8px' }}>
          <Col span={7}>
            {renderVgmViaOdex()}
          </Col>
          <Col span={1} />
          <Col span={7}>
            {renderVGMWeight()}
          </Col>
          <Col span={1} />
          {isHaz && <Col span={7}>
            {renderIMONo({
              key: `containerDetails[${containerKey}].imoNo`,
              label: <>IMO No.<Asterisk /></>,
              rules: [{ required: true, message: 'Required' }],
              initialValue: productIMOClassCodeMap?.[selectedCargoType],
              colSpan: {
                left: 9,
                right: 15,
              },
            })}
          </Col>}
          {isODC && <Col span={7}>
            {renderOdcUnits()}
          </Col>}
        </Row>
        {isHaz && <Row style={{ paddingTop: '8px' }}>
          <Col span={7}>
            {renderUnNo({
              key: `containerDetails[${containerKey}].unNo`,
              isRequired: true,
              initialValue: containerDetails?.[containerKey]?.unNo,
              label: <>UN No.<Asterisk /></>,
            })}
          </Col>
          <Col span={1} />
          {isAdditionalHazNumbersSelected && <React.Fragment>
            <Col span={7}>
              {renderIMONo({
                key: `containerDetails[${containerKey}].imoNo1`,
                label: 'IMO No. 1',
              })}
            </Col>
            <Col span={1} />
            <Col span={7}>
              {renderUnNo({
                key: `containerDetails[${containerKey}].unNo1`,
                label: 'UN No. 1',
                colSpan: {
                  left: 9,
                  right: 15,
                },
              })}
            </Col>
          </React.Fragment>}
        </Row>}
        {isAdditionalHazNumbersSelected && <React.Fragment>
          <Row style={{ paddingTop: '8px' }}>
            <Col span={7}>
              {renderIMONo({
                key: `containerDetails[${containerKey}].imoNo2`,
                label: 'IMO No. 2',
              })}
            </Col>
            <Col span={1} />
            <Col span={7}>
              {renderUnNo({
                key: `containerDetails[${containerKey}].unNo2`,
                label: 'UN No. 2',
              })}
            </Col>
            <Col span={1} />
            <Col span={7}>
              {renderIMONo({
                key: `containerDetails[${containerKey}].imoNo3`,
                label: 'IMO No. 3',
                colSpan: {
                  left: 9,
                  right: 15,
                },
              })}
            </Col>
          </Row>
          <Row>
            <Col span={7}>
            {renderUnNo({
              key: `containerDetails[${containerKey}].unNo3`,
              label: 'UN No. 3',
            })}
            </Col>
          </Row>
        </React.Fragment>}
        {isODC && <React.Fragment>
          <Row style={{ paddingTop: '14px' }}>
            <Col span={6}>
              {renderODCDimensions({
                key: `containerDetails[${containerKey}].frontDimensions`,
                label: <>Front Dimensions<Asterisk /></>,
              })}
            </Col>
            <Col span={6}>
              {renderODCDimensions({
                key: `containerDetails[${containerKey}].backDimensions`,
                label: <>Back Dimensions<Asterisk /></>,
              })}
            </Col>
            <Col span={6}>
              {renderODCDimensions({
                key: `containerDetails[${containerKey}].rightDimensions`,
                label: <>Right Dimensions<Asterisk /></>,
              })}
            </Col>
            <Col span={6}>
              {renderODCDimensions({
                key: `containerDetails[${containerKey}].leftDimensions`,
                label: <>Left Dimensions<Asterisk /></>,
              })}
            </Col>
          </Row>
          <Row style={{ paddingTop: '8px' }}>
            <Col span={6}>
              {renderODCDimensions({
                key: `containerDetails[${containerKey}].topDimensions`,
                label: <>Top Dimensions<Asterisk /></>,
              })}
            </Col>
          </Row>
        </React.Fragment>}
      </div>
    );
  };

  const renderPackageCount = () : React.ReactElement => {
    return (
      renderLabelAndInputNumber({
        ...labelInputProps,
        colSpan: {
          left: 9,
          right: 14,
        },
        label: <>No. of Package{isPackageCountMandatory ? <Asterisk /> : null}</>,
        key: `containerDetails[${containerKey}].packageCount`,
        placeholder: 'Enter No. of Package',
        initialValue: containerDetails?.[containerKey]?.packageCount,
        max: 9990,
        rules: [
          {
            validator: (rule, value, cb) => {
              if (!isValidNumber(value) && isPackageCountMandatory) {
                cb('Required');
              } else if (value < 0) {
                cb('Must be positive');
              } else if (value > 9999) {
                cb('Maximum 9999 allowed');
              } else cb();
            },
          },
        ],
        precision: 0,
      })
    );
  }; 

  const renderDONo = () : React.ReactElement => {
    return (
      renderLabelAndInput({
        ...labelInputProps,
        colSpan: {
          left: 7,
          right: 16,
        },
        label: 'DO No.',
        key: `containerDetails[${containerKey}].doNo`,
        placeholder: 'Enter DO No.',
        initialValue: containerDetails?.[containerKey]?.doNo,
        rules: [
          { max: 50, message: 'Maximum 50 characters allowed' },
        ],
      })
    );
  };

  const renderTemp = () : React.ReactElement => {
    const isReefer = selectedCargoType === CargoType.REEFER;
    return (
      <React.Fragment>
        {renderLabelAndInputNumber({
          ...labelInputProps,
          colSpan: {
            left: 5,
            right: 9,
          },
          label: <>Temp{isReefer ? <Asterisk /> : null}</>,
          key: `containerDetails[${containerKey}].temperature`,
          initialValue: containerDetails?.[containerKey]?.temperature ?? undefined,
          placeholder: 'Enter Temp',
          rules: [
            {
              validator: (rule, value, cb) => {
                if (!(isValidNumber(value)) && isReefer) {
                  cb('Required');
                } else if (value > 999.99) {
                  cb('Must be less than or equal to 999.99');
                } else cb();
              },
            },
          ],
          max: 999.99,
        })}
        <Col span={4} style={{ padding: '8px 0 0 8px' }}>°<span style={{ fontWeight: 600 }}>C</span></Col>
      </React.Fragment>
    );
  };

  const renderVoltage = () : React.ReactElement => {
    return (
      <React.Fragment>
        {renderLabelAndInputNumber({
          ...labelInputProps,
          colSpan: {
            left: 9,
            right: 9,
          },
          label: 'Voltage',
          key: `containerDetails[${containerKey}].voltage`,
          initialValue: containerDetails?.[containerKey]?.voltage ?? undefined,
          placeholder: 'Enter Voltage',
          rules: [
            {
              validator: (rule, value, cb) => {
                if (value > 9999999.99) {
                  cb('Must be less than or equal to 9999999.99');
                } else cb();
              },
            },
          ],
          max: 9999999.99,
        })}
        <Col span={4} style={{ padding: '8px 0 0 8px', fontWeight: 600 }}>V</Col>
      </React.Fragment>
    );
  };

  const renderVehicleNumber = () : React.ReactElement => {
    return (
      renderLabelAndInput({
        ...labelInputProps,
        colSpan: {
          left: 7,
          right: 16,
        },
        label: <>Vehicle No.{isVehicleNoMandatory ? <Asterisk /> : null}</>,
        key: `containerDetails[${containerKey}].vehicleNo`,
        placeholder: 'Enter Vehicle No.',
        initialValue: containerDetails?.[containerKey]?.vehicleNo,
        rules: [
          { required: isVehicleNoMandatory, message: 'Required' },
          { max: 50, message: 'Maximum 50 characters allowed' },
        ],
      })
    );
  };

  const renderHaulier = () : React.ReactElement => {
    return (
      renderLabelAndInput({
        ...labelInputProps,
        colSpan: {
          left: 5,
          right: 16,
        },
        label: 'Haulier',
        key: `containerDetails[${containerKey}].haulier`,
        placeholder: 'Enter Haulier',
        initialValue: containerDetails?.[containerKey]?.haulier,
        rules: [
          { max: 100, message: 'Maximum 100 characters allowed' },
        ],
      })
    );
  };

  const renderDriverLicenseNo = () : React.ReactElement => {
    return (
      renderLabelAndInput({
        ...labelInputProps,
        colSpan: {
          left: 7,
          right: 16,
        },
        label: 'Driver License No.',
        key: `containerDetails[${containerKey}].driverLicenseNo`,
        placeholder: 'Enter License No.',
        initialValue: containerDetails?.[containerKey]?.driverLicenseNo,
        rules: [
          { max: 100, message: 'Maximum 100 characters allowed' },
        ],
      })
    );
  };

  const renderDriverName = () : React.ReactElement => {
    return (
      renderLabelAndInput({
        ...labelInputProps,
        colSpan: {
          left: 9,
          right: 14,
        },
        label: 'Driver Name',
        key: `containerDetails[${containerKey}].driverName`,
        placeholder: 'Enter Driver Name',
        initialValue: containerDetails?.[containerKey]?.driverName,
        rules: [
          { max: 100, message: 'Maximum 100 characters allowed' },
        ],
      })
    );
  };

  const renderRemarks = () : React.ReactElement => {
    return (
      renderLabelAndTextArea({
        ...labelInputProps,
        colSpan: {
          left: 4,
          right: 20,
        },
        label: 'Remarks (CHA)',
        key: `containerDetails[${containerKey}].remarks`,
        initialValue: containerDetails?.[containerKey]?.remarks,
        rules: [{ max: 50, message: 'Maximum 50 characters allowed' }],
        rows: 3,
        formStyles: { padding: '0 10px 0 4px' },
      })
    );
  };

  const renderOdcUnits = () : React.ReactElement => {
    const field = <Form.Item style={{ marginBottom: '12px' }}>
      {getFieldDecorator(`containerDetails[${containerKey}].odcUnits`, {
        initialValue: containerDetails?.[containerKey]?.odcUnits,
        rules: [{ required: true, message: 'Required' }],
      })(
        <Select
          getPopupContainer={(trigger: any) => trigger.parentNode}
          className={classes.selectField}
          showSearch
          allowClear
          disabled={isLoading}
          optionFilterProp="children"
          placeholder={'Enter Units'}
          dropdownMatchSelectWidth={false}
        >
          {odcUnits?.map(item =>
            <Select.Option key={item}>{item}</Select.Option>
          )}
        </Select>
      )}
    </Form.Item>;
    return renderLabelAndField(
      <>ODC Units<Asterisk /></>,
      field,
      {
        left: 9,
        right: 15,
      },
    );
  };

  const renderAdditionalDetails = () : React.ReactElement => {
    return (
      <div className={classes.containerDiv}>
        <Collapse defaultActiveKey="1" className={classes.collapse}>
          <Collapse.Panel
            header="Add Additional Details"
            key="1"
            style={{ border: 'none' }}
          >
            <Row style={{ paddingTop: '8px' }}>
              <Col span={7}>
                {renderPackageCount()}
              </Col>
              <Col span={1} />
              <Col span={7}>
                {renderDONo()}
              </Col>
              <Col span={1} />
              <Col span={7}>
                {renderTemp()}
              </Col>
            </Row>
            <Row style={{ paddingTop: '8px' }}>
              <Col span={7}>
                {renderVoltage()}
              </Col>
              <Col span={1} />
              <Col span={7}>
                {renderVehicleNumber()}
              </Col>
              <Col span={1} />
              <Col span={7}>
                {renderHaulier()}
              </Col>
            </Row>
            <Row style={{ paddingTop: '8px' }}>
              <Col span={7}>
                {renderDriverName()}
              </Col>
              <Col span={1} />
              <Col span={7}>
                {renderDriverLicenseNo()}
              </Col>
            </Row>
            <Row style={{ paddingTop: '8px' }}>
              <Col span={15}>
                {renderRemarks()}
              </Col>
            </Row>
          </Collapse.Panel>
        </Collapse>
      </div>
    );
  };

  const renderContainerAndAdditionalDetails = () : React.ReactElement => {
    return (
      <React.Fragment>
        {renderContainerDetails()}
        {renderAdditionalDetails()}
      </React.Fragment>
    );
  };

  return (
    <React.Fragment>
      {renderContainerAndAdditionalDetails()}
      {isRemoveContainerModalVisible && renderRemoveModalConfirmation()}
    </React.Fragment>
  );
};

const mapStateToProps = ({ masterData }, ownProps) => ({
  containerTypes: masterData.odexMasterData?.containerTypes,
  containerSizes: masterData.odexMasterData?.containerSize,
  cargoTypes: masterData.odexMasterData?.cargoTypes,
  weightUnits: masterData.odexMasterData?.weightUnits,
  productIMOClassCodeMap: masterData.odexMasterData?.productIMOClassCodeMap,
  imoClassCodes: masterData.odexMasterData?.imoClassCodes,
  cargoToContainerListMap: masterData.execution?.cargoToContainerListMap,
  odcUnits: masterData.odexMasterData?.odcUnits,
});

export default connect(
  mapStateToProps,
  null,
  )(React.memo(withStyles(Form13Styles)(Form13UploadContainerDetails)));